import {
	Box,
	Building2,
	ChevronRight,
	ChevronsUpDown,
	FileText,
	LayoutGrid,
	LogOut,
	Menu,
	Settings,
	Truck,
	UserCog,
	type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import {
	useOrganizationSwitchSync,
	useSessionOrganizationId,
} from '../hooks/useOrganizationSwitch';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { CurrencySelector } from '../components/CurrencySelector';
import { NotificationBell } from '../components/NotificationBell';
import { OrganizationSwitcher } from '../components/OrganizationSwitcher';
import { Separator } from '../components/ui/separator';
import { ThemeToggle } from '../components/ui/theme-toggle';
import { cn } from '../lib/utils';
import { OrganizationUserType, useAuth } from '../hooks/useAuth';

/* ── Navigation structure ── */

const NAV_GROUPS = [
	{
		label: 'Workspace',
		items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/dashboard' }],
	},
	{
		label: 'Creation',
		items: [{ id: 'products', label: 'Products', icon: Box, path: '/products' }],
	},
	{
		label: 'Supply Chain',
		items: [
			{ id: 'quotes', label: 'Quotes', icon: FileText, path: '/quotes' },
			{ id: 'orders', label: 'Orders', icon: Truck, path: '/orders' },
		],
	},
	{
		label: 'Organization',
		items: [
			/* The account itself — company, addresses and the brand kits. Brands
			   live inside this page now, so a brand's own page keeps the item
			   lit; hence `alsoMatch`. */
			{
				id: 'profile',
				label: 'Profile',
				icon: Building2,
				path: '/profile',
				alsoMatch: ['/brands'],
			},
			{ id: 'users', label: 'Users', icon: UserCog, path: '/users' },
			{ id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
		],
	},
];

const TITLE_MAP: Record<string, string> = {
	dashboard: 'Dashboard',
	products: 'Products',
	profile: 'Profile',
	quotes: 'Quotes',
	orders: 'Orders',
	users: 'Users',
	settings: 'Settings',
};

/* ── Avatar ── */

function Avatar({ name = 'User', size = 26 }: { name?: string; size?: number }) {
	const initials = name
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
	return (
		<div
			className="flex shrink-0 items-center justify-center rounded-full border border-line-1 bg-surface-fill font-mono font-medium tracking-[0.04em] text-text-2"
			style={{
				width: size,
				height: size,
				fontSize: size * 0.4,
			}}
		>
			{initials}
		</div>
	);
}

/* ── Nav Item ── */

function NavItem({
	icon: Icon,
	label,
	active,
	onClick,
}: {
	icon: LucideIcon;
	label: string;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<button
			onClick={onClick}
			className={cn(
				'relative mb-px flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left font-sans text-sm font-[450] transition-[background,color] duration-[120ms] ease-out',
				active
					? 'bg-nav-active text-text-1'
					: 'text-text-2 hover:bg-nav-hover hover:text-text-1'
			)}
		>
			{active && (
				<span className="absolute -left-2 bottom-1/4 top-1/4 w-0.5 rounded-sm bg-accent" />
			)}
			<Icon size={15} className={cn('shrink-0', active ? 'text-text-1' : 'text-text-3')} />
			<span className="flex-1">{label}</span>
		</button>
	);
}

/* ── Sidebar ── */

function Sidebar({
	activeId,
	activePath,
	onNav,
	user,
	onLogout,
	sessionOrganizationId,
}: {
	activeId: string;
	activePath: string;
	sessionOrganizationId: string | undefined;
	onNav: (id: string) => void;
	user: OrganizationUserType | null;
	onLogout: () => void;
}) {
	const orgName = user?.organization?.name || '05 Creation';
	const userName = user
		? `${user.organizationUser?.firstName || ''} ${user.organizationUser?.lastName || ''}`.trim() ||
			user.email
		: 'User';

	return (
		<aside className="flex h-full flex-col overflow-hidden border-r border-line-1 bg-sidebar">
			{/* Brand — doubles as the organization switcher for multi-org users */}
			<OrganizationSwitcher
				orgName={orgName}
				currentOrganizationId={sessionOrganizationId}
				returnPath={activePath}
			/>

			{/* Nav groups */}
			<nav className="scroll-thin flex-1 overflow-y-auto px-2">
				{NAV_GROUPS.map((group) => (
					<div key={group.label} className="mb-3.5">
						<div className="px-2.5 pt-1.5 pb-1 text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
							{group.label}
						</div>
						{group.items.map((item) => (
							<NavItem
								key={item.id}
								icon={item.icon}
								label={item.label}
								active={activeId === item.id}
								onClick={() => onNav(item.id)}
							/>
						))}
					</div>
				))}
			</nav>

			{/* Bottom: user */}
			<div className="border-t border-line-1 px-2 pt-2.5 pb-3">
				<DropdownMenu>
					<DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-nav-hover focus-visible:outline-none data-[popup-open]:bg-nav-hover">
						<Avatar name={userName} size={26} />
						<div className="min-w-0 flex-1">
							<div className="truncate text-xs font-medium">{userName}</div>
							<div className="text-xs capitalize text-text-3">
								{user?.role?.toLowerCase() || 'User'}
							</div>
						</div>
						<ChevronsUpDown size={14} className="shrink-0 text-text-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent side="top" align="start" className="w-[188px]">
						<DropdownMenuItem variant="destructive" onClick={onLogout}>
							<LogOut size={14} /> Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</aside>
	);
}

/* ── Topbar ── */

function Topbar({
	activeId,
	orgName,
	onMenuToggle,
}: {
	activeId: string;
	orgName: string;
	onMenuToggle?: () => void;
}) {
	return (
		<div className="flex h-14 shrink-0 items-center gap-4 border-b border-line-1 px-4 md:px-6">
			<button
				onClick={onMenuToggle}
				className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground md:hidden"
			>
				<Menu size={18} />
			</button>
			<div className="flex items-center gap-2 text-xs text-text-3">
				<span>{orgName}</span>
				<ChevronRight size={12} />
				<span className="font-medium text-text-1">{TITLE_MAP[activeId] || activeId}</span>
			</div>
			<div className="flex-1" />
			<CurrencySelector />
			<Separator orientation="vertical" className="h-4 bg-line-1" />
			<ThemeToggle />
			<NotificationBell />
		</div>
	);
}

/* ── Layout ── */

export function PlatformLayout() {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	useScrollRestoration(scrollRef);

	const activeId =
		NAV_GROUPS.flatMap((g) => g.items).find((item) =>
			[item.path, ...(('alsoMatch' in item && item.alsoMatch) || [])].some((path) =>
				location.pathname.startsWith(path)
			)
		)?.id || 'dashboard';

	// Where a switch of organization lands: the top of the current section.
	// A detail page (a quote, a brand) belongs to the old organization.
	const activePath =
		NAV_GROUPS.flatMap((g) => g.items).find((item) => item.id === activeId)?.path ??
		'/dashboard';
	const sessionOrganizationId = useSessionOrganizationId();
	useOrganizationSwitchSync(sessionOrganizationId, activePath);

	const handleNav = (id: string) => {
		setSidebarOpen(false);
		const item = NAV_GROUPS.flatMap((g) => g.items).find((i) => i.id === id);
		if (item) navigate(item.path);
	};

	const orgName = user?.organization?.name || '05 Creation';

	return (
		<div className="relative h-full w-full overflow-hidden bg-canvas font-sans text-text-1 md:grid md:grid-cols-[var(--sidebar-width)_1fr]">
			{/* Mobile overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 z-40 bg-black/60 md:hidden"
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Sidebar — fixed drawer on mobile, static on md+ */}
			<div
				className={cn(
					'fixed inset-y-0 left-0 z-50 w-[var(--sidebar-width)] transition-transform duration-200 ease-out md:static md:translate-x-0',
					sidebarOpen ? 'translate-x-0' : '-translate-x-full'
				)}
			>
				<Sidebar
					activeId={activeId}
					activePath={activePath}
					onNav={handleNav}
					user={user}
					onLogout={logout}
					sessionOrganizationId={sessionOrganizationId}
				/>
			</div>

			<main className="flex h-full flex-col overflow-hidden bg-canvas">
				<Topbar
					activeId={activeId}
					orgName={orgName}
					onMenuToggle={() => setSidebarOpen(true)}
				/>
				<div ref={scrollRef} className="scroll-thin flex-1 overflow-auto">
					<Outlet />
				</div>
			</main>
		</div>
	);
}
